import styled from "@emotion/native"
import { StackNavigationProp } from "@react-navigation/stack"
import { type FC, type SVGProps, useMemo, useState } from "react"
import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  View,
  useWindowDimensions,
} from "react-native"
import BankIllustration from "../assets/new-onboarding/bank.svg"
import MoneyIllustration from "../assets/new-onboarding/money.svg"
import PapersIllustration from "../assets/new-onboarding/papers.svg"
import StarIllustration from "../assets/new-onboarding/star.svg"
import SynchPayLogo from "../assets/new-onboarding/synchpay.svg"
import { RootStackParamList } from "../router/types"
import { ButtonV2 } from "../ui-components/ButtonV2"
import { Text } from "../ui-components/Typography"
import { SafeAreaView } from "../ui-components/SafeAreaView"

type InitialIntroductionNavigationProp = StackNavigationProp<RootStackParamList, "Initial">

type InitialIntroductionScreenProps = {
  navigation: InitialIntroductionNavigationProp
}

type IntroSlide = {
  id: string
  titleParts: {
    text: string
    isBlue?: boolean
  }[]
  description: string
  Illustration: FC<SVGProps<SVGElement>>
  illustrationLayout: {
    left: number
    right: number
    top: number
  }
}

const INTRO_SLIDES: IntroSlide[] = [
  {
    id: "intro-1",
    titleParts: [{ text: "Save", isBlue: true }, { text: " big on your\npayments" }],
    description:
      "Simplify your financial life with a centralized billing inbox, one-tap payments, and easy recordkeeping.",
    Illustration: MoneyIllustration,
    illustrationLayout: {
      left: 55,
      right: 55,
      top: 60,
    },
  },
  {
    id: "intro-2",
    titleParts: [{ text: "Bank to bank transfers,\nit's that " }, { text: "easy", isBlue: true }],
    description:
      "SynchPay works by moving money directly from your bank account to the merchant's.",
    Illustration: BankIllustration,
    illustrationLayout: {
      left: 66,
      right: 80,
      top: 10,
    },
  },
  {
    id: "intro-3",
    titleParts: [
      { text: "Easy", isBlue: true },
      { text: " reimbursement\nfor your medical bills" },
    ],
    description: "We submit to your insurance on your behalf with one tap",
    Illustration: PapersIllustration,
    illustrationLayout: {
      left: 86,
      right: 26,
      top: 30,
    },
  },
  {
    id: "intro-4",
    titleParts: [
      { text: "Send payments " },
      { text: "faster", isBlue: true },
      { text: "\nthan ever before" },
    ],
    description: "Our payments settle by the time you finish reading this sentence.",
    Illustration: StarIllustration,
    illustrationLayout: {
      left: 78,
      right: 58,
      top: 50,
    },
  },
]

export function InitialIntroductionScreen({ navigation }: InitialIntroductionScreenProps) {
  const { width, height } = useWindowDimensions()
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)

  const slideWidth = useMemo(() => Math.max(width, 1), [width])
  const scaleX = useMemo(() => width / 430, [width])
  const scaleY = useMemo(() => height / 932, [height])

  const updateActiveSlideIndex = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetX = event.nativeEvent.contentOffset.x
    const index = Math.round(offsetX / slideWidth)
    const boundedIndex = Math.max(0, Math.min(index, INTRO_SLIDES.length - 1))
    setActiveSlideIndex((current) => (current === boundedIndex ? current : boundedIndex))
  }

  const navigateToInitialV2 = () => {
    navigation.navigate("InitialV2")
  }

  return (
    <Container>
      <TopLogoContainer>
        <SynchPayLogo />
      </TopLogoContainer>
      <ContentContainer>
        <FlatList
          data={INTRO_SLIDES}
          keyExtractor={(slide) => slide.id}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          bounces={false}
          onMomentumScrollEnd={updateActiveSlideIndex}
          {...(Platform.OS === "web"
            ? { onScroll: updateActiveSlideIndex, scrollEventThrottle: 16 }
            : {})}
          renderItem={({ item }) => (
            <Slide style={{ width: slideWidth }}>
              <ImageContainer>
                <CircleBackground />
                <IllustrationContainer
                  style={{
                    left: item.illustrationLayout.left * scaleX,
                    right: item.illustrationLayout.right * scaleX,
                    top: item.illustrationLayout.top * scaleY,
                  }}
                >
                  <item.Illustration />
                </IllustrationContainer>
              </ImageContainer>
              <SlideTitleContainer>
                <Text
                  color="primary"
                  fontSize={32}
                  lineHeight={38.4}
                  fontFamily="FFFAcidGrotesk-Medium"
                  align="center"
                >
                  {item.titleParts.map((part, index) => (
                    <Text
                      key={`${item.id}-title-${index}`}
                      color={part.isBlue ? "royalBlue" : "primary"}
                      fontSize={32}
                      lineHeight={38.4}
                      fontFamily="FFFAcidGrotesk-Medium"
                    >
                      {part.text}
                    </Text>
                  ))}
                </Text>
              </SlideTitleContainer>
              <SlideDescriptionContainer>
                <Text
                  color="primary"
                  fontSize={18}
                  lineHeight={26}
                  fontFamily="FFFAcidGrotesk-Book"
                  align="center"
                >
                  {item.description}
                </Text>
              </SlideDescriptionContainer>
            </Slide>
          )}
        />
      </ContentContainer>

      <BottomSection>
        <Indicators>
          {INTRO_SLIDES.map((slide, index) => (
            <IndicatorLine key={slide.id} isActive={index === activeSlideIndex} />
          ))}
        </Indicators>
        <ButtonV2
          title="Get started"
          testID="get-started"
          accessibilityLabel="Get started"
          onPress={navigateToInitialV2}
          variant="blue2"
          customHeight={60}
        />
      </BottomSection>
    </Container>
  )
}

const Container = styled(SafeAreaView)`
  flex: 1;
  background-color: #f5f6f8;
`

const ContentContainer = styled.View`
  flex: 1;
`

const TopLogoContainer = styled.View`
  align-items: center;
  justify-content: center;
  padding-top: 12px;
`

const Slide = styled.View`
  flex: 1;
  align-items: center;
  justify-content: flex-start;
  padding: 24px;
`

const ImageContainer = styled.View`
  width: 100%;
  height: 300px;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
`

const CircleBackground = styled.View`
  position: absolute;
  width: 243px;
  height: 243px;
  border-radius: 121.5px;
  background-color: #dde6fc;
`

const IllustrationContainer = styled.View`
  position: absolute;
  align-items: center;
  justify-content: center;
  background-color: transparent;
`

const SlideTitleContainer = styled.View`
  align-items: center;
`

const SlideDescriptionContainer = styled.View`
  margin-top: 32px;
`

const BottomSection = styled.View`
  padding: 16px 20px 24px;
  width: 100%;
  max-width: 500px;
  align-self: center;
`

const Indicators = styled(View)`
  flex-direction: row;
  justify-content: center;
  margin-bottom: 20px;
  gap: 8px;
`

const IndicatorLine = styled.View<{ isActive: boolean }>`
  width: 41px;
  height: 8px;
  border-radius: 999px;
  background-color: ${({ isActive }) => (isActive ? "#3f5ad9" : "#d6dced")};
`
